import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  formatLatexPreview,
  formatFractionLatexCore
} from "../../../../components/inputs/FractionInput";

// ========== Pomocnicze funkcje ==========

function getRandomInt(min, max, exclude = []) {
  let value;
  do {
    value = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(value));
  return value;
}

function gcd(a, b) {
  a = Math.round(a);
  b = Math.round(b);
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return Math.abs(a);
}

function formatFraction(numerator, denominator) {
  if (denominator === 0 || !Number.isFinite(denominator)) return "undefined";

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}

function formatSignedCoefficient(n, variable) {
  if (n === 1) return `+ ${variable}`;
  if (n === -1) return `- ${variable}`;
  return `${n < 0 ? "- " + Math.abs(n) : "+ " + n}${variable}`;
}

// ========== Generator zadania ==========

export function generateConvertGeneralToSlopeTask() {
  let A = 0, B = 0, C = 0;
  do {
    A = getRandomInt(-9, 9, [0]);
    B = getRandomInt(-9, 9, [0]); // B ≠ 0
    C = getRandomInt(-9, 9, [0]);
  } while (B === 0);

  const g = gcd(gcd(A, B), C);
  const divisor = !Number.isFinite(g) || g === 0 ? 1 : g;

  return {
    A: A / divisor,
    B: B / divisor,
    C: C / divisor
  };
}

// ========== Renderowanie treści ==========

export function renderTask(input) {
  const { A, B, C } = input.data || input;

  const aPart = `${A === 1 ? "" : A === -1 ? "-" : A}x`;
  const bPart = formatSignedCoefficient(B, "y");
  const cPart = C < 0 ? `- ${Math.abs(C)}` : `+ ${C}`;

  return (
    <p>
      Przekształć równanie ogólne:{" "}
      <InlineMath math={`${aPart} ${bPart} ${cPart} = 0`} /> do postaci kierunkowej{" "}
      <InlineMath math="y = ax + b" /> i podaj wartości współczynników{" "}
      <InlineMath math="a" /> oraz <InlineMath math="b" />.
    </p>
  );
}

export function renderAnswer(task) {
  const { A, B, C } = task.data || task;

  const rawA = formatFraction(-A, B);
  const rawB = formatFraction(-C, B);

  const ax =
    rawA === "1" ? "x" : rawA === "-1" ? "-x" : `${rawA}x`;

  const bClean = rawB.startsWith("-") ? rawB.slice(1) : rawB;
  const sign = rawB.startsWith("-") ? "-" : "+";

  return (
    <div className="space-y-3">
      <div style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
        <InlineMath math={`a = ${rawA}`} />
      </div>
      <div style={{ marginBottom: "1.5rem" }}>
        <InlineMath math={`b = ${rawB}`} />
      </div>
      <div>
        <strong>Postać kierunkowa równania prostej:</strong>
        <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
          <InlineMath math={`\\displaystyle y = ${ax} ${sign} ${bClean}`} />
        </div>
      </div>
    </div>
  );
}

export function renderExplanation(input) {
  const { A, B, C } = input.data || input;

  const formatTerm = (coef, variable, isFirst = false) => {
    if (coef === 0) return "";
    if (coef === 1) return isFirst ? `${variable}` : `+ ${variable}`;
    if (coef === -1) return `- ${variable}`;
    return `${coef > 0 && !isFirst ? "+ " : ""}${coef}${variable}`;
  };

  const a = formatFraction(-A, B);
  const b = formatFraction(-C, B);
  const ax = a === "1" ? "x" : a === "-1" ? "-x" : `${a}x`;
  const signB = b.startsWith("-") ? "- " + b.slice(1) : "+ " + b;

  const step1 =
    `${formatTerm(A, "x", true)} ${formatTerm(B, "y")} ${C === 0 ? "" : C > 0 ? "+ " + C : "- " + Math.abs(C)}`;

  const left = `${B === 1 ? "y" : B === -1 ? "-y" : `${B}y`}`;
  const negA = -A;
  const negC = -C;
  const right =
    `${formatTerm(negA, "x", true)} ${negC === 0 ? "" : negC > 0 ? "+ " + negC : "- " + Math.abs(negC)}`;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Przepisujemy równanie ogólne:
        <BlockMath math={`${step1.trim()} = 0`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Przenosimy wszystkie składniki oprócz wyrażenia z <InlineMath math="y" /> na drugą stronę:
        <BlockMath math={`${left} = ${right.trim()}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Dzielimy obie strony przez <InlineMath math={`${B}`} />:
        <BlockMath math={`y = ${ax} ${signB}`} />
      </div>
    </div>
  );
}


export function checkAnswer(input, userInput) {
  const { A, B, C } = input.data || input;
  const correctA = -A / B;
  const correctB = -C / B;

  const parsedA = userInput?.a?.parsed;
  const parsedB = userInput?.b?.parsed;

  const isCorrect = parsedA !== null && parsedB !== null &&
    Math.abs(parsedA - correctA) < 1e-6 &&
    Math.abs(parsedB - correctB) < 1e-6;

  return {
    isCorrect,
    expected: `a = ${formatFraction(-A, B)}, b = ${formatFraction(-C, B)}`
  };
}



export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <InlineMath math="a=" />
        <FractionInput
          value={userInput?.a || ""}
          onChange={(val) => onChange({ ...userInput, a: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="b=" />
        <FractionInput
          value={userInput?.b || ""}
          onChange={(val) => onChange({ ...userInput, b: val })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.a?.raw ||
    !userAnswer.b?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latexA = formatLatexPreview(userAnswer.a.raw, "a");
  const latexB = formatLatexPreview(userAnswer.b.raw, "b");

  const aCore = formatFractionLatexCore(userAnswer.a.raw);
  const bCore = formatFractionLatexCore(userAnswer.b.raw);

  const bClean = bCore.startsWith("-") ? bCore.slice(1) : bCore;
  const sign = bCore.startsWith("-") ? "-" : "+";

  const equation = `y = ${aCore}x ${sign} ${bClean}`;

  return (
<div className="flex flex-wrap gap-y-4">
  <div style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
    <InlineMath math={latexA} />
  </div>
  <div>
      <div style={{ marginBottom: "1rem" }}>
    <InlineMath math={latexB} />
  </div>
  </div>

      <div className="pt-2">
        <strong>Równanie prostej:</strong>{" "}
        <InlineMath math={equation} />
      </div>
    </div>
  );
}

